import { IPromise, IQService } from "angular";
import { ITask, TaskService } from "@gtmhub/tasks";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/goalService";
import { MetricService, IMetric } from "@gtmhub/okrs/metrics";
import { TeamService, ITeam } from "@gtmhub/teams";
import { IUser } from "@gtmhub/users/models";
import { UserService } from "@gtmhub/users/userService";
import { StateService } from "@uirouter/angularjs";

export type SupportedUpdateItem = ITask | IGoal | IMetric | IUser | ITeam;

export class UpdateService {
  public static $inject = ["$q", "GoalService", "MetricService", "TaskService", "UserService", "TeamService", "$state"];

  constructor(
    private $q: IQService,
    private goalService: GoalService,
    private metricService: MetricService,
    private taskService: TaskService,
    private userService: UserService,
    private teamService: TeamService,
    private $state: StateService
  ) {}

  public create(itemType: string, item: ITask | IGoal | IMetric): IPromise<ITask | IGoal | IMetric> {
    switch (itemType) {
      case "goal":
        return this.createGoal(item as IGoal);
      case "metric":
        return this.createMetric(item as IMetric);
      case "task":
        return this.createTask(item as ITask);
      default:
        console.warn("update service does not support such items");
        // do nothing;
        return null;
    }
  }

  private createGoal(item: IGoal): IPromise<IGoal> {
    const deferred = this.$q.defer<IGoal>();
    this.goalService.createGoal(item).then(
      (newGoal: IGoal) => {
        deferred.resolve(newGoal);
      },
      (error) => {
        deferred.reject(error);
      }
    );
    return deferred.promise;
  }

  private createMetric(item: IMetric): IPromise<IMetric> {
    const deferred = this.$q.defer<IMetric>();
    this.metricService.createMetric(item).then(
      (newMetric: IMetric) => {
        deferred.resolve(newMetric);
      },
      (error) => {
        deferred.reject(error);
      }
    );
    return deferred.promise;
  }

  private createTask(item: ITask): IPromise<ITask> {
    const deferred = this.$q.defer<ITask>();
    this.taskService.create(item).then(
      (newTask: ITask) => {
        deferred.resolve(newTask);
      },
      (error) => {
        deferred.reject(error);
      }
    );

    return deferred.promise;
  }

  // does not support metric checkin update
  public update(itemType: string, item: Partial<SupportedUpdateItem>): IPromise<SupportedUpdateItem> {
    switch (itemType) {
      case "goal":
        return this.updateGoal(item as Partial<IGoal>);
      case "metric":
        return this.updateMetric(item as Partial<IMetric>);
      case "task":
        return this.updateTask(item as Partial<ITask>);
      case "user":
        return this.updateUser(item as Partial<IUser>);
      case "team":
        return this.updateTeam(item as Partial<ITeam>);
      default:
        console.warn("update service does not support such items");
        // do nothing;
        return null;
    }
  }

  private updateUser(item: Partial<IUser>): IPromise<IUser> {
    return this.userService.patchUser(item.id, item).then(
      () => null,
      (error) => this.$q.reject(error)
    );
  }

  private updateTeam(item: Partial<ITeam>): IPromise<ITeam> {
    return this.teamService.updateTeam(item).then(
      (result) => result,
      (error) => this.$q.reject(error)
    );
  }

  private updateGoal(item: Partial<IGoal>): IPromise<IGoal> {
    const deferred = this.$q.defer<IGoal>();
    const includeMetrics = this.$state.current.name.includes(".tree.");
    this.goalService.patchGoal(item.id, item, { includeMetrics: includeMetrics }).then(
      (updatedGoal: IGoal) => {
        deferred.resolve(updatedGoal);
      },
      (error) => {
        deferred.reject(error);
      }
    );
    return deferred.promise;
  }

  private updateMetric(item: IMetric): IPromise<IMetric> {
    const deferred = this.$q.defer<IMetric>();
    this.metricService.patchMetric(item).then(
      (updatedMetric: IMetric) => {
        deferred.resolve(updatedMetric);
      },
      (error) => {
        deferred.reject(error);
      }
    );
    return deferred.promise;
  }

  private updateTask(item: ITask): IPromise<ITask> {
    const deferred = this.$q.defer<ITask>();
    this.taskService.update(item.id, item).then(
      (updatedTask: ITask) => {
        deferred.resolve(updatedTask);
      },
      (error) => {
        deferred.reject(error);
      }
    );

    return deferred.promise;
  }
}
